import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { EdocumentPaymentWay } from "@/atv/eInvoiceApi";
import { CARD_PAYMENT_WAY_CODE, HEALTH_SERVICE_CABYS_PREFIXES, PAYMENT_METHODS, REDUCED_4_TAX_PERCENTAGE_CODE } from "./Constants";

/** Los montos del XML de Hacienda son Decimal 18,5. */
const IVA_DEVUELTO_DECIMALS = 5;

const roundIvaDevuelto = (value: number): number => {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Number(value.toFixed(IVA_DEVUELTO_DECIMALS));
};

const getLineCabys = (line: EDocumentProductDataType): string =>
    `${line.codigo_cabys || line.product?.codigo_cabys || ""}`.trim();

/**
 * Una linea aplica para el IVA Devuelto cuando es un servicio de salud humana
 * (CAByS del grupo 931) facturado con la tarifa reducida del 4%. Hacienda valida
 * ambas condiciones y rechaza el comprobante si el monto no corresponde.
 */
export const isHealthServiceLine = (line: EDocumentProductDataType): boolean => {
    const cabys = getLineCabys(line);
    if (!cabys) {
        return false;
    }
    const isHealthCabys = HEALTH_SERVICE_CABYS_PREFIXES.some((prefix) => cabys.startsWith(prefix));
    return isHealthCabys && `${line.codigo_tarifa}` == REDUCED_4_TAX_PERCENTAGE_CODE;
};

/** Suma del IVA de las lineas de servicios de salud con tarifa del 4%. */
export const getHealthServicesTax = (products: EDocumentProductDataType[]): number =>
    (products || []).reduce(
        (total, line) => (isHealthServiceLine(line) ? total + Number(line.impuestos || 0) : total),
        0
    );

/**
 * Convierte los pagos persistidos del documento (formaPago interno) al formato
 * de medios de pago del comprobante electronico.
 */
export const toEdocumentPaymentWays = <T extends { formaPago?: string | null, monto?: number | null, vuelto?: number | null }>(
    documentPayments: T[]
): EdocumentPaymentWay[] =>
    (documentPayments || []).map((payment) => ({
        tipo: PAYMENT_METHODS[Number(payment.formaPago)].atvCode,
        monto: Number(payment.monto || 0) - Number(payment.vuelto || 0)
    }));

/** Monto total cubierto con tarjeta segun los medios de pago del documento. */
export const getCardPaymentAmount = (paymentWays: EdocumentPaymentWay[]): number => {
    const cardAtvCode = PAYMENT_METHODS[CARD_PAYMENT_WAY_CODE].atvCode;
    return (paymentWays || []).reduce(
        (total, payment) => (payment.tipo == cardAtvCode ? total + Number(payment.monto || 0) : total),
        0
    );
};

/**
 * Calcula el campo "TotalIVADevuelto" del resumen del comprobante (anexo v4.4,
 * pagina 55 y nota al pie 16).
 *
 * - Si toda la venta son servicios de salud pagados con tarjeta, es la suma del
 *   IVA de esas lineas.
 * - En ventas mixtas (varios medios de pago y/o otros productos):
 *   `(monto pagado con tarjeta / venta neta) * IVA de los servicios de salud`.
 * - Nunca puede superar el IVA de las lineas de salud al 4%.
 */
export const calculateTotalIVADevuelto = (
    products: EDocumentProductDataType[],
    paymentWays: EdocumentPaymentWay[],
    ventaNeta: number
): number => {
    const healthServicesTax = getHealthServicesTax(products);
    if (healthServicesTax <= 0) {
        return 0;
    }

    const cardAmount = getCardPaymentAmount(paymentWays);
    if (cardAmount <= 0) {
        return 0;
    }

    const netSale = Number(ventaNeta || 0);
    if (netSale <= 0) {
        return 0;
    }

    const isCardOnly = cardAmount >= netSale;
    const isHealthOnly = (products || []).every((line) => isHealthServiceLine(line));
    if (isCardOnly && isHealthOnly) {
        return roundIvaDevuelto(healthServicesTax);
    }

    const proportionalAmount = (cardAmount / netSale) * healthServicesTax;
    return roundIvaDevuelto(Math.min(proportionalAmount, healthServicesTax));
};
